import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LogOut, ChevronRight, Shield, Sprout, MapPin, Phone, User, Edit, Save, X, Building2, CreditCard, Landmark, Heart, Bell, Camera, Loader2, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { trackPageView } from '@/lib/analytics';
import { API_BASE } from '@/lib/apiBase';
import OrgRegisterSheet from '@/components/OrgRegisterSheet';
import InstallLink from '@/components/InstallLink';
import MyPostsFeed from '@/components/MyPostsFeed';
import Avatar from '@/components/Avatar';

const BUSINESS_TYPES = ['Restaurant', 'Retailer', 'Wholesaler', 'Distributor', 'Food Processor', 'Exporter', 'Other'];

const PROFILE_TAB_KEY = 'frootex_profile_tab';

export default function ProfilePage() {
  const { user, logout, refreshUser, api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    const stored = localStorage.getItem(PROFILE_TAB_KEY);
    return stored === 'about' ? 'about' : 'posts';
  });
  useEffect(() => { localStorage.setItem(PROFILE_TAB_KEY, tab); }, [tab]);
  const [editing, setEditing] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [bankForm, setBankForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [quickName, setQuickName] = useState('');
  const [joinOrgOpen, setJoinOrgOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joiningOrg, setJoiningOrg] = useState(false);
  const [registerOrgOpen, setRegisterOrgOpen] = useState(false);
  const avatarInputRef = useRef(null);

  useEffect(() => { trackPageView('profile'); }, []);

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        email: user.email || '',
        location: user.location || '',
        address: user.address || '',
        pincode: user.pincode || '',
        business_name: user.business_name || '',
        business_type: user.business_type || '',
        gstin: user.gstin || '',
      });
      setBankForm({
        bank_account_number: user.bank_account_number || '',
        bank_ifsc: user.bank_ifsc || '',
        bank_account_holder: user.bank_account_holder || '',
        bank_name: user.bank_name || '',
        upi_id: user.upi_id || '',
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const updates = {};
      Object.entries(profileForm).forEach(([k, v]) => { if (v) updates[k] = v; });
      await api.put('/profile', updates);
      await refreshUser();
      setEditing(false);
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handleSaveBank = async () => {
    setSaving(true);
    try {
      const updates = {};
      Object.entries(bankForm).forEach(([k, v]) => { if (v) updates[k] = v; });
      await api.put('/profile/bank', updates);
      await refreshUser();
      setEditingBank(false);
      toast.success('Bank details updated!');
    } catch { toast.error('Failed to update bank details'); }
    finally { setSaving(false); }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: uploaded } = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const avatarUrl = `${API_BASE}/api/files/${uploaded.storage_path}`;
      await api.put('/profile', { avatar: avatarUrl });
      await refreshUser();
      toast.success('Profile picture updated!');
    } catch {
      toast.error('Failed to update profile picture');
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleQuickSaveName = async (e) => {
    e.preventDefault();
    const trimmed = quickName.trim();
    if (!trimmed) {
      toast.error('Please enter your name');
      return;
    }
    setSavingName(true);
    try {
      await api.put('/profile', { name: trimmed });
      await refreshUser();
      setQuickName('');
      toast.success('Name saved!');
    } catch {
      toast.error('Failed to save name');
    } finally {
      setSavingName(false);
    }
  };

  const roleBadge = () => {
    if (user?.role === 'farmer') {
      return (
        <span className="px-2 py-0.5 bg-[#15803D]/10 text-[#15803D] text-xs font-bold rounded-md flex items-center gap-1">
          <Shield className="w-3 h-3" /> Verified Farmer
        </span>
      );
    }
    if (user?.role === 'admin') {
      return (
        <span className="px-2 py-0.5 bg-[#0B4F3F]/10 text-[#0B4F3F] text-xs font-bold rounded-md flex items-center gap-1">
          <Shield className="w-3 h-3" /> Frootex Admin
        </span>
      );
    }
    return <span className="px-2 py-0.5 bg-[#EFF2F7] text-[#5B6675] text-xs font-semibold rounded-md">Vendor</span>;
  };

  const maskAccount = (acc) => acc ? `****${acc.slice(-4)}` : '';

  const handleJoinOrg = async (e) => {
    e.preventDefault();
    const code = joinCode.trim().toUpperCase();
    if (code.length < 4) {
      toast.error('Please enter a valid org code');
      return;
    }
    setJoiningOrg(true);
    try {
      const { data } = await api.post('/orgs/join', { org_code: code });
      toast.success(data.message || 'Joined organization');
      setJoinOrgOpen(false);
      setJoinCode('');
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join — check the code');
    } finally {
      setJoiningOrg(false);
    }
  };

  return (
    <AppShell title="Profile">
      <div className="p-4 space-y-5" data-testid="profile-page">
        {/* Avatar & Basic Info */}
        <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-5 text-center animate-fade-in-up">
          <div className="relative inline-block">
            <Avatar
              name={user?.name}
              src={user?.avatar}
              size={80}
              className="mx-auto border-2 border-[#E4E8EF]"
              testId="profile-avatar-img"
            />
            <button
              type="button"
              data-testid="upload-avatar-btn"
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingAvatar}
              aria-label="Change profile picture"
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#0B4F3F] text-[#F6F8FB] flex items-center justify-center border-2 border-[#E4E8EF] shadow-md active:scale-95 transition-transform disabled:opacity-70"
            >
              {uploadingAvatar ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
            </button>
            <input
              ref={avatarInputRef}
              data-testid="avatar-file-input"
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </div>

          {user?.name ? (
            <h2 className="text-xl font-bold text-[#0B1620] mt-3 font-['Bricolage_Grotesque']" data-testid="profile-name">{user.name}</h2>
          ) : (
            <form
              onSubmit={handleQuickSaveName}
              className="mt-3 flex gap-2"
              data-testid="quick-name-form"
            >
              <Input
                data-testid="quick-name-input"
                value={quickName}
                onChange={(e) => setQuickName(e.target.value)}
                placeholder="Add your name"
                autoFocus
                className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm text-[#0B1620]"
              />
              <Button
                type="submit"
                data-testid="quick-name-save-btn"
                disabled={savingName || !quickName.trim()}
                className="h-10 px-4 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-lg text-xs font-semibold"
              >
                {savingName ? 'Saving…' : 'Save'}
              </Button>
            </form>
          )}
          <div className="flex items-center justify-center gap-2 mt-2">{roleBadge()}</div>
          {user?.user_code && (
            <p className="text-[11px] text-[#5B6675] mt-2 font-mono tracking-wide flex items-center justify-center gap-1">
              <span className="opacity-70">Frootex ID</span>
              <span className="text-[#0B1620] font-semibold" data-testid="profile-user-code">{user.user_code}</span>
            </p>
          )}
          {user?.phone && (
            <p className="text-sm text-[#5B6675] mt-2 flex items-center justify-center gap-1">
              <Phone className="w-3.5 h-3.5" /> {user.phone}
            </p>
          )}
          {user?.location && (
            <p className="text-sm text-[#5B6675] mt-1 flex items-center justify-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {user.location}
            </p>
          )}
        </div>

        {/* FB-inspired tab bar */}
        <div
          role="tablist"
          data-testid="profile-tabs"
          className="relative grid grid-cols-2 gap-1 p-1 bg-[#EFF2F7] rounded-full border border-[#E4E8EF] animate-fade-in-up"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'posts'}
            data-testid="profile-tab-posts"
            onClick={() => setTab('posts')}
            className={`relative z-10 h-10 rounded-full flex items-center justify-center gap-1.5 text-[12px] font-semibold transition-all duration-200 ${
              tab === 'posts'
                ? 'bg-[#F6F8FB] text-[#0B1620] shadow-[0_2px_8px_-2px_rgba(11,22,32,0.08)]'
                : 'text-[#5B6675] hover:text-[#0B1620]'
            }`}
          >
            Posts
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'about'}
            data-testid="profile-tab-about"
            onClick={() => setTab('about')}
            className={`relative z-10 h-10 rounded-full flex items-center justify-center gap-1.5 text-[12px] font-semibold transition-all duration-200 ${
              tab === 'about'
                ? 'bg-[#F6F8FB] text-[#0B1620] shadow-[0_2px_8px_-2px_rgba(11,22,32,0.08)]'
                : 'text-[#5B6675] hover:text-[#0B1620]'
            }`}
          >
            About
          </button>
        </div>

        {tab === 'posts' && <MyPostsFeed />}

        {tab === 'about' && (
          <>

        {/* Profile Details (editable) */}
        <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4 animate-fade-in-up stagger-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque'] flex items-center gap-2">
              <User className="w-4 h-4 text-[#0B4F3F]" /> Personal Details
            </h3>
            {editing ? (
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="text-xs text-[#5B6675] flex items-center gap-1"><X className="w-3.5 h-3.5" /> Cancel</button>
                <button data-testid="save-profile-btn" onClick={handleSaveProfile} disabled={saving} className="text-xs text-[#0B4F3F] font-semibold flex items-center gap-1">
                  <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            ) : (
              <button data-testid="edit-profile-btn" onClick={() => setEditing(true)} className="text-xs text-[#0B4F3F] flex items-center gap-1"><Edit className="w-3.5 h-3.5" /> Edit</button>
            )}
          </div>

          {editing ? (
            <div className="space-y-3">
              {[
                { key: 'name', label: 'Full Name', icon: User, placeholder: 'Your name' },
                { key: 'email', label: 'Email', icon: null, placeholder: 'email@example.com', type: 'email' },
                { key: 'location', label: 'City / State', icon: MapPin, placeholder: 'Mumbai, Maharashtra' },
                { key: 'address', label: 'Full Address', icon: null, placeholder: 'Street, area, landmark' },
                { key: 'pincode', label: 'Pincode', icon: null, placeholder: '400001' },
                { key: 'business_name', label: 'Business Name', icon: Building2, placeholder: 'Your business name' },
                { key: 'gstin', label: 'GSTIN (optional)', icon: null, placeholder: 'e.g., 22AAAAA0000A1Z5' },
              ].map(({ key, label, placeholder, type }) => (
                <div key={key}>
                  <Label className="text-xs text-[#5B6675] mb-1 block">{label}</Label>
                  <Input
                    data-testid={`profile-${key}-input`}
                    type={type || 'text'}
                    value={profileForm[key]}
                    onChange={(e) => setProfileForm(p => ({ ...p, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm text-[#0B1620]"
                  />
                </div>
              ))}
              <div>
                <Label className="text-xs text-[#5B6675] mb-1 block">Business Type</Label>
                <Select value={profileForm.business_type} onValueChange={(v) => setProfileForm(p => ({ ...p, business_type: v }))}>
                  <SelectTrigger data-testid="profile-business-type" className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F6F8FB] border-[#E4E8EF]">
                    {BUSINESS_TYPES.map(t => <SelectItem key={t} value={t.toLowerCase()}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="space-y-2.5">
              {[
                { label: 'Email', value: user?.email },
                { label: 'Location', value: user?.location },
                { label: 'Address', value: user?.address },
                { label: 'Pincode', value: user?.pincode },
                { label: 'Business', value: user?.business_name },
                { label: 'Type', value: user?.business_type },
                { label: 'GSTIN', value: user?.gstin },
              ].map(({ label, value }) => value ? (
                <div key={label} className="flex items-center justify-between text-sm">
                  <span className="text-[#5B6675]">{label}</span>
                  <span className="text-[#0B1620] font-medium">{value}</span>
                </div>
              ) : null)}
              {!user?.email && !user?.location && !user?.business_name && (
                <p className="text-xs text-[#5B6675]/60 text-center py-2">Tap Edit to add your details</p>
              )}
            </div>
          )}
        </div>

        {/* Bank Details Section */}
        <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4 animate-fade-in-up stagger-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque'] flex items-center gap-2">
              <Landmark className="w-4 h-4 text-[#0B4F3F]" /> Bank Details
            </h3>
            {editingBank ? (
              <div className="flex gap-2">
                <button onClick={() => setEditingBank(false)} className="text-xs text-[#5B6675] flex items-center gap-1"><X className="w-3.5 h-3.5" /> Cancel</button>
                <button data-testid="save-bank-btn" onClick={handleSaveBank} disabled={saving} className="text-xs text-[#0B4F3F] font-semibold flex items-center gap-1">
                  <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            ) : (
              <button data-testid="edit-bank-btn" onClick={() => setEditingBank(true)} className="text-xs text-[#0B4F3F] flex items-center gap-1"><Edit className="w-3.5 h-3.5" /> Edit</button>
            )}
          </div>

          {editingBank ? (
            <div className="space-y-3">
              {[
                { key: 'bank_account_holder', label: 'Account Holder Name', placeholder: 'Name as per bank' },
                { key: 'bank_account_number', label: 'Account Number', placeholder: 'Enter account number' },
                { key: 'bank_ifsc', label: 'IFSC Code', placeholder: 'e.g., SBIN0001234' },
                { key: 'bank_name', label: 'Bank Name', placeholder: 'e.g., State Bank of India' },
                { key: 'upi_id', label: 'UPI ID (optional)', placeholder: 'e.g., name@upi' },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <Label className="text-xs text-[#5B6675] mb-1 block">{label}</Label>
                  <Input
                    data-testid={`bank-${key}-input`}
                    value={bankForm[key]}
                    onChange={(e) => setBankForm(p => ({ ...p, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm text-[#0B1620]"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2.5">
              {user?.bank_account_holder && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5B6675]">Holder</span>
                  <span className="text-[#0B1620] font-medium">{user.bank_account_holder}</span>
                </div>
              )}
              {user?.bank_account_number && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5B6675]">Account</span>
                  <span className="text-[#0B1620] font-medium flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> {maskAccount(user.bank_account_number)}</span>
                </div>
              )}
              {user?.bank_ifsc && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5B6675]">IFSC</span>
                  <span className="text-[#0B1620] font-medium">{user.bank_ifsc}</span>
                </div>
              )}
              {user?.bank_name && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5B6675]">Bank</span>
                  <span className="text-[#0B1620] font-medium">{user.bank_name}</span>
                </div>
              )}
              {user?.upi_id && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5B6675]">UPI</span>
                  <span className="text-[#0B1620] font-medium">{user.upi_id}</span>
                </div>
              )}
              {!user?.bank_account_number && !user?.upi_id && (
                <p className="text-xs text-[#5B6675]/60 text-center py-2">No bank details added yet</p>
              )}
            </div>
          )}
        </div>

        {/* Menu Items */}
        <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] overflow-hidden animate-fade-in-up stagger-3">
          <button
            data-testid="liked-farmers-nav-btn"
            onClick={() => navigate('/liked')}
            className="w-full flex items-center gap-3 p-4 hover:bg-[#EFF2F7] transition-colors border-b border-[#E4E8EF]"
          >
            <div className="w-9 h-9 rounded-xl bg-[#B91C1C]/10 flex items-center justify-center">
              <Heart className="w-4 h-4 text-[#B91C1C] fill-[#B91C1C]" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-[#0B1620]">Liked Farmers</p>
              <p className="text-xs text-[#5B6675]">Farmers you follow for updates</p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#5B6675]" />
          </button>
          <button
            data-testid="my-requirements-nav-btn"
            onClick={() => navigate('/my-requirements')}
            className="w-full flex items-center gap-3 p-4 hover:bg-[#EFF2F7] transition-colors border-b border-[#E4E8EF]"
          >
            <div className="w-9 h-9 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center">
              <ShoppingBag className="w-4 h-4 text-[#0B4F3F]" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-[#0B1620]">My requirements</p>
              <p className="text-xs text-[#5B6675]">Manage produce you&apos;re looking to buy</p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#5B6675]" />
          </button>
          {user?.role === 'admin' && (
            <button
              data-testid="admin-panel-nav-btn"
              onClick={() => navigate('/admin')}
              className="w-full flex items-center gap-3 p-4 hover:bg-[#EFF2F7] transition-colors border-b border-[#E4E8EF]"
            >
              <div className="w-9 h-9 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center">
                <Shield className="w-4 h-4 text-[#0B4F3F]" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-[#0B1620]">Admin panel</p>
                <p className="text-xs text-[#5B6675]">Moderation, KYC, users, requirements</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#5B6675]" />
            </button>
          )}
          <button
            data-testid="notifications-nav-btn"
            onClick={() => navigate('/notifications')}
            className="w-full flex items-center gap-3 p-4 hover:bg-[#EFF2F7] transition-colors border-b border-[#E4E8EF]"
          >
            <div className="w-9 h-9 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center">
              <Bell className="w-4 h-4 text-[#0B4F3F]" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-[#0B1620]">Notifications</p>
              <p className="text-xs text-[#5B6675]">Updates & activity</p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#5B6675]" />
          </button>
          {user?.role === 'vendor' && (
            <button
              data-testid="become-farmer-btn"
              onClick={() => navigate('/kyc-apply')}
              className="w-full flex items-center gap-3 p-4 hover:bg-[#EFF2F7] transition-colors border-b border-[#E4E8EF]"
            >
              <div className="w-9 h-9 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center">
                <Sprout className="w-4 h-4 text-[#0B4F3F]" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-[#0B1620]">Become a Farmer</p>
                <p className="text-xs text-[#5B6675]">Get verified to sell produce</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#5B6675]" />
            </button>
          )}
          {(user?.role === 'farmer' || user?.role === 'admin') && (
            <button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate('/farmer-dashboard')}
              className="w-full flex items-center gap-3 p-4 hover:bg-[#EFF2F7] transition-colors border-b border-[#E4E8EF]"
            >
              <div className="w-9 h-9 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center">
                <Sprout className="w-4 h-4 text-[#0B4F3F]" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-[#0B1620]">Farmer Dashboard</p>
                <p className="text-xs text-[#5B6675]">Manage your produce posts</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#5B6675]" />
            </button>
          )}
          {user?.role === 'org_admin' && (
            <button
              data-testid="org-dashboard-nav-btn"
              onClick={() => navigate('/org')}
              className="w-full flex items-center gap-3 p-4 hover:bg-[#EFF2F7] transition-colors border-b border-[#E4E8EF]"
            >
              <div className="w-9 h-9 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-[#0B4F3F]" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-[#0B1620]">My Organization</p>
                <p className="text-xs text-[#5B6675]">Manage farmers, requests & analytics</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#5B6675]" />
            </button>
          )}
          {(user?.role === 'vendor' || user?.role === 'farmer') && !user?.org_id && (
            <>
              <button
                data-testid="join-org-btn"
                onClick={() => setJoinOrgOpen(true)}
                className="w-full flex items-center gap-3 p-4 hover:bg-[#EFF2F7] transition-colors border-b border-[#E4E8EF]"
              >
                <div className="w-9 h-9 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-[#0B4F3F]" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-[#0B1620]">Join an organization</p>
                  <p className="text-xs text-[#5B6675]">Enter your 6-char org code</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#5B6675]" />
              </button>
              <button
                data-testid="register-org-btn"
                onClick={() => setRegisterOrgOpen(true)}
                className="w-full flex items-center gap-3 p-4 hover:bg-[#EFF2F7] transition-colors border-b border-[#E4E8EF]"
              >
                <div className="w-9 h-9 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-[#0B4F3F]" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-[#0B1620]">Register as Enterprise</p>
                  <p className="text-xs text-[#5B6675]">FPO / Cooperative / Aggregator</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#5B6675]" />
              </button>
            </>
          )}
          <InstallLink />
        </div>

        {/* Join-org dialog */}
        {joinOrgOpen && (
          <div
            className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            data-testid="join-org-dialog"
            onClick={() => !joiningOrg && setJoinOrgOpen(false)}
          >
            <form
              onClick={(e) => e.stopPropagation()}
              onSubmit={handleJoinOrg}
              className="w-full max-w-sm bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-5 space-y-3 animate-fade-in-up"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0B4F3F]" />
                <h3 className="text-base font-semibold text-[#0B1620] font-['Bricolage_Grotesque']">Join an organization</h3>
              </div>
              <p className="text-xs text-[#5B6675]">Paste the 6-character code your org admin shared with you.</p>
              <Input
                data-testid="join-org-code-input"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={10}
                className="h-11 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-base font-mono font-bold tracking-widest text-center text-[#0B1620]"
                autoFocus
              />
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  onClick={() => setJoinOrgOpen(false)}
                  variant="outline"
                  className="flex-1 h-10 border-[#E4E8EF] rounded-full text-sm"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="join-org-submit-btn"
                  type="submit"
                  disabled={joinCode.trim().length < 4 || joiningOrg}
                  className="flex-1 h-10 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full text-sm font-semibold"
                >
                  {joiningOrg ? 'Joining…' : 'Join'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Logout */}
        <Button
          data-testid="logout-btn"
          onClick={handleLogout}
          variant="outline"
          className="w-full h-12 border-[#E4E8EF] text-[#B91C1C] hover:bg-[#B91C1C]/5 rounded-full font-semibold"
        >
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>

          </>
        )}

        <OrgRegisterSheet
          open={registerOrgOpen}
          onOpenChange={setRegisterOrgOpen}
          onRegistered={() => navigate('/org')}
        />
      </div>
    </AppShell>
  );
}
